import React from "react";
import { Box, Button } from "@mui/material";
import { Add as AddIcon } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import colors from "../../../shared/themes/colors";
import { PublicServiceRequest } from "../models/models";
import { ServiceRequestsState } from "../../servicing/models/models";
import ServiceRequestCard from "./ServiceRequestCard";
import useTranslation from "../../../shared/hooks/useTranslation";
import { useAppSelector } from "../../../shared/hooks/sharedHooks";
import TableEmpty from "../../../shared/components/tables/TableEmpty";
import BoxSearchIcon from "../../../shared/assets/icons/BoxSearchIcon";
import PermissionGuard from "../../../shared/components/PermissionGuard";
import { Modules, PermissionLevel } from "../../../core/models/PermissionModels";
import { dt, accentGradientDark, accentGradientDarkHover } from "../../../shared/themes/redesignTokens";

interface ServiceBoardContentProps {
  serviceRequests: PublicServiceRequest[];
  starredIds?: string[];
  onViewDetails?: (serviceRequest: PublicServiceRequest) => void;
  onUnpublish?: (serviceRequest: PublicServiceRequest) => void;
  onMakeOffer?: (serviceRequest: PublicServiceRequest) => void;
  onBookmark?: (serviceRequest: PublicServiceRequest) => void;
  changedIds?: Set<string>;
  isOwnerOnly?: boolean;
}

interface DateGroup {
  date: string;
  formattedDate: string;
  serviceRequests: PublicServiceRequest[];
}

const ServiceBoardContent = ({
  serviceRequests,
  starredIds,
  onViewDetails,
  onUnpublish,
  onMakeOffer,
  onBookmark,
  changedIds,
  isOwnerOnly,
}: ServiceBoardContentProps) => {
  const { translate } = useTranslation(["serviceBoard"]);
  const navigate = useNavigate();

  // Fetch service request stats from Redux (already loaded by Header)
  const { serviceRequestStats } = useAppSelector<ServiceRequestsState>(
    (state: any) => state.customerServicing
  );
  const totalRequests = serviceRequestStats?.totalRequests ?? 0;

  // Group service requests by open date
  const groupByDate = (requests: PublicServiceRequest[]): DateGroup[] => {
    const groups: Record<string, PublicServiceRequest[]> = {};

    requests.forEach((request) => {
      const dateKey = request.openDate
        ? dayjs(request.openDate).format("YYYY-MM-DD")
        : "no-date";

      if (!groups[dateKey]) {
        groups[dateKey] = [];
      }
      groups[dateKey].push(request);
    });

    // Sort dates descending (newest first)
    const sortedDates = Object.keys(groups).sort((a, b) => {
      if (a === "no-date") return 1;
      if (b === "no-date") return -1;
      return b.localeCompare(a);
    });

    return sortedDates.map((date) => ({
      date,
      formattedDate: formatDateHeader(date),
      serviceRequests: groups[date]
    }));
  };

  const formatDateHeader = (dateKey: string): string => {
    if (dateKey === "no-date") {
      return translate("noDateSpecified");
    }

    const date = dayjs(dateKey);
    const today = dayjs();
    const yesterday = today.subtract(1, "day");

    if (date.isSame(today, "day")) {
      return translate("today");
    }

    if (date.isSame(yesterday, "day")) {
      return translate("yesterday");
    }

    // Format: Monday, January 19, 2026
    return date.format("dddd, MMMM D, YYYY");
  };

  if (!serviceRequests?.length) {
    // Owner with active requests elsewhere (progressed past board stage)
    const hasActiveRequestsElsewhere = isOwnerOnly && totalRequests > 0;

    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
          minHeight: "400px",
          backgroundColor: colors.secondary.gray96
        }}
      >
        <TableEmpty
          iconComponent={BoxSearchIcon}
          text={
            hasActiveRequestsElsewhere
              ? translate("noOpenRequests")
              : isOwnerOnly
                ? translate("noOwnServiceRequests")
                : translate("noServiceRequestsFound")
          }
          secondaryText={
            hasActiveRequestsElsewhere
              ? undefined
              : isOwnerOnly
                ? translate("createRequestToSeeHere")
                : translate("tryAdjustingFilters")
          }
        >
          {hasActiveRequestsElsewhere && (
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2, mt: 1 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  fontSize: "15px",
                  color: dt.textSecondary,
                  fontFamily: dt.font,
                }}
              >
                <span>
                  {translate("servicesInProgress", { count: totalRequests })}
                </span>
                <Box
                  component="span"
                  onClick={() => navigate("/service-requests")}
                  sx={{
                    color: dt.blue,
                    fontWeight: 600,
                    cursor: "pointer",
                    fontFamily: dt.font,
                    transition: "opacity 0.2s",
                    "&:hover": { opacity: 0.8 },
                  }}
                >
                  {translate("viewServiceRequests")}
                </Box>
              </Box>
              <PermissionGuard module={Modules.Servicing} level={PermissionLevel.Write}>
                <Button
                  variant="contained"
                  startIcon={<AddIcon sx={{ fontSize: "16px !important" }} />}
                  onClick={() => navigate("/service-requests/new")}
                  sx={{
                    background: accentGradientDark,
                    borderRadius: dt.radiusPill,
                    padding: "8px 24px",
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: "13px",
                    fontFamily: dt.font,
                    letterSpacing: "0.2px",
                    boxShadow: "0 2px 8px rgba(52,152,219,0.25)",
                    border: "none",
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    "&:hover": {
                      background: accentGradientDarkHover,
                      transform: "translateY(-1px)",
                      boxShadow: "0 4px 12px rgba(52,152,219,0.3)",
                    },
                  }}
                >
                  {translate("createServiceRequest")}
                </Button>
              </PermissionGuard>
            </Box>
          )}
        </TableEmpty>
      </Box>
    );
  }

  const dateGroups = groupByDate(serviceRequests);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      {dateGroups.map((group) => (
        <Box key={group.date}>
          {/* Date separator with lines and centered pill */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              mb: 1.5
            }}
          >
            <Box
              sx={{
                flex: 1,
                height: "1px",
                backgroundColor: "#F3F4F6"
              }}
            />
            <Box
              sx={{
                color: "#6B7280",
                borderRadius: "10px",
                border: "1px solid #E5E7EB",
                backgroundColor: "#FAFBFC",
                padding: "2px 10px",
                whiteSpace: "nowrap",
                fontFamily: 'var(--font-mono)',
                fontSize: "11px",
                fontWeight: 500,
                letterSpacing: "0.3px",
              }}
            >
              {group.formattedDate}
            </Box>
            <Box
              sx={{
                flex: 1,
                height: "1px",
                backgroundColor: "#F3F4F6"
              }}
            />
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {group.serviceRequests.map((serviceRequest) => (
              <Box
                key={serviceRequest.id}
                sx={changedIds?.has(serviceRequest.id) ? { animation: "rowHighlight 1.5s ease-out", borderRadius: "12px" } : undefined}
              >
                <ServiceRequestCard
                  serviceRequest={serviceRequest}
                  isStarred={starredIds?.includes(serviceRequest.id)}
                  onViewDetails={onViewDetails}
                  onUnpublish={onUnpublish}
                  onMakeOffer={onMakeOffer}
                  onBookmark={onBookmark}
                />
              </Box>
            ))}
          </Box>
        </Box>
      ))}
    </Box>
  );
};

export default ServiceBoardContent;
